import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import LoginDropdown from "../features/auth/components/login-dropdown"
import FileUploadInput from "../features/auth/components/file-upload-input"
import BaseButton from "../components/Button/base-button"
import {
  CsvImportResponse,
  useCsvImportV2Mutation,
} from "../app/services/meseeknaApiV2"
import NotificationContainer from "../features/auth/components/Notifications/NotificationContainer"
import Table from "../components/table"

type ImportUserFormProps = {
  groupsList: { value: string; label: string }[]
  handleCancel: () => void
  disableGroup: boolean
  defaultGroup: string
  onCreateGroup?: () => void
}

const columns = [{ name: "Name" }, { name: "Email" }, { name: "Remarks" }]

const ImportUserForm: React.FC<ImportUserFormProps> = ({
  groupsList,
  handleCancel,
  disableGroup,
  defaultGroup,
  onCreateGroup,
}) => {
  const navigate = useNavigate()

  const [selectedGroup, setSelectedGroup] = useState<string>(defaultGroup)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [emailStatus, setEmailStatus] = useState<boolean>(true)
  const [fileInputErrorMsg, setFileInputErrorMsg] = useState<string>("")
  const [incorrectData, setIncorrectData] = useState<
    {
      firstName: string
      lastName: string
      email: string
      role: string
      remarks: string
    }[]
  >([])
  const [successMessage, setSuccessMessage] = useState<string>("")
  const [errorMessage, setErrorMessage] = useState<string>("")
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [csvImportV2] = useCsvImportV2Mutation()
  const handleSelectGroup = (value: string) => {
    if (value === "none") {
      setSelectedGroup("")
    } else {
      setSelectedGroup(value)
    }
  }

  const handleSubmit = async () => {
    console.log("CSV Import: handleSubmit called", { selectedFile, selectedGroup, emailStatus })

    if (!selectedFile) {
      setFileInputErrorMsg("Please upload CSV file")
      return
    }

    // Build FormData for v2 endpoint
    const formData = new FormData()
    formData.append("userImport", selectedFile)
    if (selectedGroup) {
      formData.append("groupId", selectedGroup)
    }
    formData.append("emailStatus", emailStatus ? "1" : "0")

    setSuccessMessage("")
    setErrorMessage("")
    setIsSubmitting(true)

    console.log("CSV Import: Starting upload...")

    try {
      const result = await csvImportV2(formData)
      console.log("CSV Import: Response received", result)

      // RTK Query returns { data } on success or { error } on failure
      if ("error" in result) {
        const errorData = result.error as { data?: { message?: string }; status?: number; error?: string }
        console.error("CSV Import: Error in result", errorData)

        // Handle various error formats
        let errorMsg = "Failed to import CSV. Please try again."
        if (errorData?.data?.message) {
          errorMsg = errorData.data.message
        } else if (typeof errorData === 'object' && 'error' in errorData && errorData.error) {
          errorMsg = String(errorData.error)
        } else if (errorData?.status === 401) {
          errorMsg = "Session expired. Please refresh the page and try again."
        } else if (errorData?.status === 403) {
          errorMsg = "You don't have permission to import users."
        }

        setErrorMessage(errorMsg)
        setIsSubmitting(false)
        return
      }

      const response = result.data as CsvImportResponse
      console.log("CSV Import: Success response", JSON.stringify(response, null, 2))

      if (response.status === "success") {
        if (response.errorData && response.errorData.length > 0) {
          setIncorrectData(response.errorData)
          setSuccessMessage(
            `${
              response.insertedMail ? response.insertedMail.length : 0
            } participants imported${emailStatus ? " and invitation emails sent" : ""}`,
          )
        } else {
          setSuccessMessage(
            emailStatus
              ? "Participants imported successfully and invitation emails sent!"
              : "Participants imported successfully."
          )
          handleCancel()
        }
      } else {
        setErrorMessage(response.message || "Import failed. Please check your CSV file format.")
      }
    } catch (error) {
      console.error("CSV Import: Caught exception", error)
      const errorMsg = error instanceof Error ? error.message : "An unexpected error occurred. Please try again."
      setErrorMessage(errorMsg)
    } finally {
      setIsSubmitting(false)
    }
  }

  const getUsersData = () => {
    const preparedUsersData =
      incorrectData.length > 0
        ? incorrectData.map(
            (
              user: {
                firstName: string
                lastName: string
                email: string
                role: string
                remarks: string
              },
              index: number,
            ) => {
              return [
                {
                  id: (index * 3 + 1).toString(),
                  name: "name",
                  value: `${user.firstName} ${user.lastName}`,
                },
                {
                  id: (index * 3 + 2).toString(),
                  name: "email",
                  value: user.email,
                },
                {
                  id: (index * 3 + 3).toString(),
                  name: "remarks",
                  value: user.remarks,
                },
              ]
            },
          )
        : []
    return preparedUsersData
  }

  return (
    <React.Fragment>
      <NotificationContainer errorMessage={errorMessage} successMessage={successMessage} />
      {incorrectData.length === 0 ? (
        <section className="py-10 px-8 lg:py-16 w-6/12 mx-auto">
          <div className="mx-auto lg:container">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 self-stretch flex flex-col justify-start gap-[24px] p-8 font-text-md-regular">
              {/* Header Section */}
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Add Participant List</h1>
                <p className="text-gray-600">
                  Upload a CSV with participant names, emails, and roles to add multiple users at once.
                </p>
              </div>

              {/* CSV Template Download */}
              <a
                href="/v3/assets/sample_user_CSV.csv"
                download="sample_user_CSV.csv"
                className="inline-flex items-center gap-2 text-[#6182F7] hover:text-[#4d6ad9] font-medium">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download CSV Template
              </a>

              {/* File Upload - with border for contrast */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <FileUploadInput
                  id="uploadCsv"
                  inputTitleText="Upload CSV"
                  inputPlaceholderText="No file chosen"
                  acceptInputTypes=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                  required={true}
                  disabled={isSubmitting}
                  selectedFile={selectedFile}
                  error={fileInputErrorMsg}
                  setSelectedFile={(file: File | null) => {
                    setFileInputErrorMsg("")
                    setSelectedFile(file)
                  }}
                />
              </div>

              {/* Group Selection */}
              <div>
                <LoginDropdown
                  id="importUserSelectGroup"
                  titleText="Select Group"
                  placeholderText="Select group name"
                  options={groupsList}
                  defaultValue={selectedGroup}
                  setValue={(value: string) => handleSelectGroup(value)}
                  showNoneOption={true}
                  disable={isSubmitting || disableGroup}
                />
                {/* Create New Group link */}
                {!disableGroup && groupsList.length === 0 && (
                  <p className="text-sm text-gray-500 mt-2">
                    No groups available.{" "}
                    <button
                      type="button"
                      onClick={() => {
                        if (onCreateGroup) {
                          onCreateGroup()
                        } else {
                          handleCancel()
                          navigate("/dashboard/recruiter-admin/groups")
                        }
                      }}
                      className="text-[#6182F7] hover:underline font-medium"
                    >
                      Create a new group
                    </button>
                  </p>
                )}
                {!disableGroup && groupsList.length > 0 && (
                  <p className="text-sm text-gray-500 mt-2">
                    Need a new group?{" "}
                    <button
                      type="button"
                      onClick={() => {
                        if (onCreateGroup) {
                          onCreateGroup()
                        } else {
                          handleCancel()
                          navigate("/dashboard/recruiter-admin/groups")
                        }
                      }}
                      className="text-[#6182F7] hover:underline font-medium"
                    >
                      Create one
                    </button>
                  </p>
                )}
              </div>

              {/* Email checkbox */}
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={emailStatus}
                  onChange={(e) => setEmailStatus(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-[#6182F7] focus:ring-[#6182F7]"
                  disabled={isSubmitting}
                />
                <span className="text-gray-700">Send invitation email to participants</span>
              </label>

              {/* Action Buttons */}
              <div className="self-stretch flex flex-row items-center justify-center gap-4 mt-4 pt-4 border-t border-gray-200">
                <BaseButton
                  variant="secondary"
                  onClick={handleCancel}
                  className="w-full">
                  Cancel
                </BaseButton>
                <BaseButton
                  variant="primary"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  className="w-full"
                  type="submit">
                  Import Participants
                </BaseButton>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="py-10 px-8 lg:py-16 w-9/12 mx-auto">
          <div className="mx-auto lg:container">
            <div className="bg-white rounded-lg shadow-card self-stretch flex flex-col justify-start gap-[24px] font-text-md-regular">
              <div className="flex items-center justify-between">
                <h1>Incorrect CSV Data</h1>
                <BaseButton
                  variant="primary"
                  onClick={handleCancel}
                  loading={isSubmitting}
                  className="w-fit"
                  type="submit">
                  Continue
                </BaseButton>
              </div>
              <Table
                columns={columns}
                data={getUsersData()}
                newData={getUsersData().map((userArray, index) => ({
                  check: false,
                  index: index,
                  usersDetails: userArray,
                }))}
                showCheckboxColumn={false}
                showSearch={false}
                disablePagination={incorrectData.length < 6}
                addPadding={false}
              />
            </div>
          </div>
        </section>
      )}
    </React.Fragment>
  )
}

export default ImportUserForm
